import React, { useState, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

// Copy of the main app's client/src/components/AddRequirementModal.js (plan 18.3). Same
// debugLog trim as CreateStandardModal.js: console.error replaces debugError so this file
// does not need a utils/debugLog.js this phase does not own.
export default function AddRequirementModal({ isOpen, onClose, onSubmit, isSubmitting, standardName }) {
  const [requirementData, setRequirementData] = useState({
    section: '',
    subsection: '',
    title: '',
    description: '',
    criteria: '',
    mandatory: true,
    weight: 1.0,
    parent_section: '',
  });

  const [errors, setErrors] = useState({});

  const resetForm = useCallback(() => {
    setRequirementData({
      section: '',
      subsection: '',
      title: '',
      description: '',
      criteria: '',
      mandatory: true,
      weight: 1.0,
      parent_section: '',
    });
    setErrors({});
  }, []);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validateForm = () => {
    const newErrors = {};

    if (!requirementData.section.trim()) {
      newErrors.section = 'Section is required';
    }
    if (!requirementData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!requirementData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!requirementData.criteria.trim()) {
      newErrors.criteria = 'Criteria is required';
    }
    if (requirementData.weight < 0.1 || requirementData.weight > 10.0) {
      newErrors.weight = 'Weight must be between 0.1 and 10.0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(requirementData);
      resetForm();
    } catch (error) {
      console.error('Failed to add requirement:', error?.response?.status || error?.message);
    }
  };

  const updateField = (field, value) => {
    setRequirementData((prev) => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Add Requirement
            </h3>
            <p className="text-sm text-gray-500">
              Add a new requirement to {standardName}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isSubmitting}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col max-h-[calc(90vh-8rem)]">
          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="space-y-4">
              {/* Section and Subsection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Section *
                  </label>
                  <input
                    type="text"
                    value={requirementData.section}
                    onChange={(e) => updateField('section', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.section ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., C01"
                    disabled={isSubmitting}
                  />
                  {errors.section && <p className="text-red-500 text-xs mt-1">{errors.section}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subsection
                  </label>
                  <input
                    type="text"
                    value={requirementData.subsection}
                    onChange={(e) => updateField('subsection', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional subsection"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={requirementData.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Requirement title"
                  disabled={isSubmitting}
                />
                {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  value={requirementData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.description ? 'border-red-500' : 'border-gray-300'
                  }`}
                  rows={3}
                  placeholder="Detailed description"
                  disabled={isSubmitting}
                />
                {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
              </div>

              {/* Assessment Criteria */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assessment Criteria *
                </label>
                <textarea
                  value={requirementData.criteria}
                  onChange={(e) => updateField('criteria', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.criteria ? 'border-red-500' : 'border-gray-300'
                  }`}
                  rows={3}
                  placeholder="How this requirement will be evaluated"
                  disabled={isSubmitting}
                />
                {errors.criteria && <p className="text-red-500 text-xs mt-1">{errors.criteria}</p>}
              </div>

              {/* Properties */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="mandatory"
                    checked={requirementData.mandatory}
                    onChange={(e) => updateField('mandatory', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    disabled={isSubmitting}
                  />
                  <label htmlFor="mandatory" className="ml-2 block text-sm text-gray-700">
                    Mandatory
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Weight
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="10.0"
                    value={requirementData.weight}
                    onChange={(e) => updateField('weight', parseFloat(e.target.value) || 1.0)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.weight ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={isSubmitting}
                  />
                  {errors.weight && <p className="text-red-500 text-xs mt-1">{errors.weight}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Parent Section
                  </label>
                  <input
                    type="text"
                    value={requirementData.parent_section}
                    onChange={(e) => updateField('parent_section', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional parent"
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 flex-shrink-0">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-[#F8F8FF] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Adding...' : 'Add Requirement'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
